import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { 
  DollarSign, CreditCard, CheckCircle, AlertCircle, ExternalLink, 
  Clock, ArrowRight, Shield, Loader2, Download,
  Info, RefreshCw, ChevronLeft, ChevronRight, Eye, ArrowDownRight
} from 'lucide-react';
import { Input } from './ui/input';
import { connectAPI, ledgerAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const PayoutsTab = () => {
  const { toast } = useToast();
  const { getAuthToken } = useAuth();
  
  // State
  const [connectStatus, setConnectStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState({ entries: [], pagination: {} });
  const [commissionRates, setCommissionRates] = useState(null);
  
  // VAT/Tax State
  const [vatNumber, setVatNumber] = useState('');
  const [vatSaving, setVatSaving] = useState(false);
  const [vatLoaded, setVatLoaded] = useState(false);

  // UI State
  const [loading, setLoading] = useState(true);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      
      const [statusData, summaryData, historyData, ratesData] = await Promise.all([
        connectAPI.getStatus('host', token),
        ledgerAPI.getHostSummary(token),
        ledgerAPI.getHostHistory(token, 1, 20, null),
        connectAPI.getCommissionRates()
      ]);
      
      setConnectStatus(statusData);
      setSummary(summaryData);
      setHistory(historyData);
      setCommissionRates(ratesData);
    } catch (error) {
      console.error('Error fetching payout data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load payout information',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, toast]);

  // Load VAT info from profile
  useEffect(() => {
    if (!vatLoaded) {
      const loadVat = async () => {
        try {
          const token = getAuthToken();
          const res = await axios.get(`${API_URL}/api/users/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setVatNumber(res.data?.vatNumber || '');
          setVatLoaded(true);
        } catch (e) { /* ok */ }
      };
      loadVat();
    }
  }, [getAuthToken, vatLoaded]);

  const handleSaveVat = async () => {
    setVatSaving(true);
    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/api/users/profile`, { vatNumber }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Saved', description: 'VAT number updated successfully.' });
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to save VAT number.', variant: 'destructive' });
    } finally {
      setVatSaving(false);
    }
  };

  // Fetch history with filters
  const fetchHistory = useCallback(async (page, filter) => {
    try {
      const token = getAuthToken();
      const data = await ledgerAPI.getHostHistory(token, page, 20, filter);
      setHistory(data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  }, [getAuthToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchHistory(historyPage, statusFilter);
  }, [historyPage, statusFilter, fetchHistory]);

  // Handlers
  const handleStartOnboarding = async () => {
    setOnboardingLoading(true);
    try {
      const token = getAuthToken();
      const returnUrl = `${window.location.origin}/host/dashboard?tab=payouts&onboarding=complete`;
      const refreshUrl = `${window.location.origin}/host/dashboard?tab=payouts&onboarding=refresh`;
      
      const result = await connectAPI.initiateOnboarding('host', returnUrl, refreshUrl, token);
      
      if (result.onboardingUrl) {
        window.location.href = result.onboardingUrl;
      } else if (result.onboardingComplete) {
        toast({
          title: 'Already Onboarded',
          description: 'Your payout account is already set up!'
        });
        fetchData();
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to start onboarding',
        variant: 'destructive'
      });
    } finally {
      setOnboardingLoading(false);
    }
  };

  const handleOpenDashboard = async () => {
    try {
      const token = getAuthToken();
      const result = await connectAPI.getDashboardLink('host', token);
      if (result.dashboardUrl) {
        window.open(result.dashboardUrl, '_blank');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to open Stripe dashboard',
        variant: 'destructive'
      });
    }
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const token = getAuthToken();
      // Fetch all entries for export
      const allData = await ledgerAPI.getHostHistory(token, 1, 1000, null);
      
      // Build CSV
      const headers = ['Date', 'Type', 'Booking Ref', 'Gross (GBP)', 'Platform Fee', 'Stripe Fee', 'VAT', 'Net (GBP)', 'Status'];
      const rows = allData.entries.map(e => [
        new Date(e.date).toLocaleDateString(),
        e.entityType,
        e.bookingRef || e.entityId,
        e.grossAmount.toFixed(2),
        e.platformFee.toFixed(2),
        e.stripeFee.toFixed(2),
        e.vatAmount.toFixed(2),
        e.netAmount.toFixed(2),
        e.status
      ]);
      
      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `earnings-export-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast({ title: 'Success', description: 'CSV exported successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to export CSV', variant: 'destructive' });
    } finally {
      setExportLoading(false);
    }
  };

  // Format currency
  const formatGBP = (amount) => {
    const num = parseFloat(amount) || 0;
    const prefix = num < 0 ? '-' : '';
    return `${prefix}£${Math.abs(num).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Status badge color
  const getStatusBadge = (status) => {
    switch (status) {
      case 'transferred':
        return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Paid</Badge>;
      case 'pending_transfer':
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Pending</Badge>;
      case 'refunded':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Refunded</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Failed</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#1F3A68]" />
      </div>
    );
  }

  const isOnboarded = connectStatus?.payoutsEnabled;

  return (
    <div className="space-y-6" data-testid="payouts-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#1F3A68]">Financial Ledger</h2>
          <p className="text-gray-600">Your accounting overview and transaction history</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw size={14} className="mr-2" />
            Refresh
          </Button>
          {isOnboarded && (
            <Button variant="outline" size="sm" onClick={handleOpenDashboard}>
              <ExternalLink size={14} className="mr-2" />
              Stripe Dashboard
            </Button>
          )}
        </div>
      </div>

      {/* Section D: Payout Status Panel */}
      {!isOnboarded ? (
        <Card className="border-2 border-amber-200 bg-amber-50" data-testid="onboarding-prompt">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <CreditCard className="h-6 w-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-amber-900 mb-1">Complete Stripe Onboarding to Receive Payouts</h3>
                <p className="text-amber-700 mb-4">
                  Connect your bank account to receive automatic payouts when guests book your properties.
                </p>
                <div className="flex flex-wrap gap-3 mb-4">
                  <div className="flex items-center gap-2 text-sm text-amber-800">
                    <Shield size={14} />
                    <span>Bank-level security</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-amber-800">
                    <Clock size={14} />
                    <span>2-3 day payouts</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-amber-800">
                    <CheckCircle size={14} />
                    <span>Automatic transfers</span>
                  </div>
                </div>
                <Button 
                  onClick={handleStartOnboarding}
                  disabled={onboardingLoading}
                  className="bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                  data-testid="start-onboarding-btn"
                >
                  {onboardingLoading ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <>
                      Get Started
                      <ArrowRight size={16} className="ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-2 border-emerald-200 bg-emerald-50" data-testid="payouts-enabled">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-6 w-6 text-emerald-600" />
              <div className="flex-1">
                <p className="font-semibold text-emerald-800">Payouts Enabled</p>
                <p className="text-sm text-emerald-700">
                  Your bank account is connected. Earnings are automatically transferred.
                </p>
              </div>
              {summary?.totalPendingTransfer > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-100 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">
                    {formatGBP(summary.totalPendingTransfer)} pending
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section A: Earnings Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4" data-testid="earnings-cards">
        <TooltipProvider>
          {/* Gross Revenue */}
          <Card className="bg-slate-50 border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setStatusFilter(null)}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <DollarSign size={18} className="text-slate-500" />
                <Tooltip>
                  <TooltipTrigger>
                    <Info size={14} className="text-slate-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Total gross revenue from all bookings before fees</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="text-xl font-bold text-slate-800">{formatGBP(summary?.totalGrossRevenue)}</div>
              <div className="text-xs text-slate-500 mt-1">Gross Revenue</div>
            </CardContent>
          </Card>

          {/* Platform Fees */}
          <Card className="bg-slate-50 border-slate-200 hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <DollarSign size={18} className="text-orange-500" />
                <Tooltip>
                  <TooltipTrigger>
                    <Info size={14} className="text-slate-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="font-semibold mb-1">Platform Fee</p>
                    <p>13% of your accommodation revenue under the host only model, so the guest pays no separate fee. Founding hosts pay 0% for their first 90 days, then 10% for the rest of year one. Any referral payout (3% to your referrer) comes out of this, not on top of it.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="text-xl font-bold text-orange-600">{formatGBP(summary?.totalPlatformFees)}</div>
              <div className="text-xs text-slate-500 mt-1">Platform Fees</div>
            </CardContent>
          </Card>

          {/* Stripe Fees */}
          <Card className="bg-slate-50 border-slate-200 hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <CreditCard size={18} className="text-purple-500" />
                <Tooltip>
                  <TooltipTrigger>
                    <Info size={14} className="text-slate-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Stripe processing fee: 2.9% + 30p per transaction</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="text-xl font-bold text-purple-600">{formatGBP(summary?.totalStripeFees)}</div>
              <div className="text-xs text-slate-500 mt-1">Stripe Fees</div>
            </CardContent>
          </Card>

          {/* VAT */}
          <Card className="bg-slate-50 border-slate-200 hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-500">VAT</span>
                <Tooltip>
                  <TooltipTrigger>
                    <Info size={14} className="text-slate-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>UK VAT collected at 20% rate</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="text-xl font-bold text-slate-600">{formatGBP(summary?.totalVAT)}</div>
              <div className="text-xs text-slate-500 mt-1">VAT Collected</div>
            </CardContent>
          </Card>

          {/* Net Earnings */}
          <Card className="bg-emerald-50 border-emerald-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setStatusFilter(null)}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <DollarSign size={18} className="text-emerald-600" />
              </div>
              <div className="text-xl font-bold text-emerald-700">{formatGBP(summary?.totalNetEarnings)}</div>
              <div className="text-xs text-emerald-600 mt-1">Net Earnings</div>
            </CardContent>
          </Card>

          {/* Pending Payout */}
          <Card className={`hover:shadow-md transition-shadow cursor-pointer ${
            summary?.totalPendingTransfer > 0 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'
          }`} onClick={() => setStatusFilter('pending_transfer')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Clock size={18} className={summary?.totalPendingTransfer > 0 ? 'text-amber-600' : 'text-slate-400'} />
              </div>
              <div className={`text-xl font-bold ${summary?.totalPendingTransfer > 0 ? 'text-amber-700' : 'text-slate-600'}`}>
                {formatGBP(summary?.totalPendingTransfer)}
              </div>
              <div className="text-xs text-slate-500 mt-1">Pending Payout</div>
            </CardContent>
          </Card>

          {/* Total Paid */}
          <Card className="bg-emerald-50 border-emerald-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setStatusFilter('transferred')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle size={18} className="text-emerald-600" />
              </div>
              <div className="text-xl font-bold text-emerald-700">{formatGBP(summary?.totalTransferred)}</div>
              <div className="text-xs text-emerald-600 mt-1">Total Paid Out</div>
            </CardContent>
          </Card>
        </TooltipProvider>
      </div>

      {/* Refunds Alert */}
      {summary?.totalRefunded > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <ArrowDownRight className="h-5 w-5 text-red-600" />
              <div>
                <p className="font-semibold text-red-800">
                  Total Refunds: {formatGBP(summary.totalRefunded)}
                </p>
                <p className="text-sm text-red-700">
                  Refunds are shown as negative entries and reduce your totals.
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="ml-auto border-red-300 text-red-700 hover:bg-red-100"
                onClick={() => setStatusFilter('refunded')}
              >
                View Refunds
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section C: Transaction History Table */}
      <Card data-testid="transaction-history">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-[#1F3A68]">Transaction History</CardTitle>
            <div className="flex gap-2">
              {/* Status Filter */}
              <select
                value={statusFilter || ''}
                onChange={(e) => {
                  setStatusFilter(e.target.value || null);
                  setHistoryPage(1);
                }}
                className="h-9 px-3 text-sm border rounded-md bg-white"
              >
                <option value="">All Status</option>
                <option value="transferred">Paid</option>
                <option value="pending_transfer">Pending</option>
                <option value="refunded">Refunded</option>
              </select>
              
              {/* Export Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                disabled={exportLoading}
                data-testid="export-csv-btn"
              >
                {exportLoading ? (
                  <Loader2 size={14} className="mr-2 animate-spin" />
                ) : (
                  <Download size={14} className="mr-2" />
                )}
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {history.entries.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No transactions found
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left p-3 font-semibold text-slate-600">Date</th>
                      <th className="text-left p-3 font-semibold text-slate-600">Type</th>
                      <th className="text-left p-3 font-semibold text-slate-600">Reference</th>
                      <th className="text-right p-3 font-semibold text-slate-600">Gross</th>
                      <th className="text-right p-3 font-semibold text-slate-600">Platform Fee</th>
                      <th className="text-right p-3 font-semibold text-slate-600">Stripe Fee</th>
                      <th className="text-right p-3 font-semibold text-slate-600">VAT</th>
                      <th className="text-right p-3 font-semibold text-slate-600">Net</th>
                      <th className="text-center p-3 font-semibold text-slate-600">Status</th>
                      <th className="text-center p-3 font-semibold text-slate-600">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.entries.map((entry) => {
                      const isRefund = entry.entityType === 'refund' || entry.netAmount < 0;
                      return (
                        <tr 
                          key={entry._id} 
                          className={`border-b hover:bg-slate-50 ${isRefund ? 'bg-red-50' : ''}`}
                          data-testid={`history-row-${entry._id}`}
                        >
                          <td className="p-3 text-slate-700">
                            {new Date(entry.date).toLocaleDateString('en-GB')}
                          </td>
                          <td className="p-3">
                            <span className={`font-medium ${isRefund ? 'text-red-700' : 'text-slate-700'}`}>
                              {isRefund ? 'Refund' : entry.entityType === 'booking' ? 'Booking' : entry.entityType}
                            </span>
                          </td>
                          <td className="p-3 text-slate-600 font-mono text-xs">
                            {entry.bookingRef || entry.entityId?.slice(-8)}
                          </td>
                          <td className={`p-3 text-right font-medium ${isRefund ? 'text-red-700' : 'text-slate-700'}`}>
                            {formatGBP(entry.grossAmount)}
                          </td>
                          <td className={`p-3 text-right ${isRefund ? 'text-red-600' : 'text-orange-600'}`}>
                            {formatGBP(entry.platformFee)}
                          </td>
                          <td className={`p-3 text-right ${isRefund ? 'text-red-600' : 'text-purple-600'}`}>
                            {formatGBP(entry.stripeFee)}
                          </td>
                          <td className="p-3 text-right text-slate-600">
                            {formatGBP(entry.vatAmount)}
                          </td>
                          <td className={`p-3 text-right font-bold ${isRefund ? 'text-red-700' : 'text-emerald-700'}`}>
                            {formatGBP(entry.netAmount)}
                          </td>
                          <td className="p-3 text-center">
                            {getStatusBadge(entry.status)}
                          </td>
                          <td className="p-3 text-center">
                            {entry.entityType === 'booking' && entry.entityId && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(`/booking/${entry.entityId}`, '_blank')}
                              >
                                <Eye size={14} />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {history.pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-slate-600">
                    Showing {((historyPage - 1) * 20) + 1} - {Math.min(historyPage * 20, history.pagination.totalEntries)} of {history.pagination.totalEntries}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                      disabled={historyPage === 1}
                    >
                      <ChevronLeft size={14} className="mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setHistoryPage(p => p + 1)}
                      disabled={!history.pagination.hasMore}
                    >
                      Next
                      <ChevronRight size={14} className="ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Commission Structure Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-[#1F3A68]">Commission Structure</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-700 mb-3">What You Keep</h4>
              <div className="space-y-2">
                <div className="flex justify-between p-3 bg-emerald-50 rounded-lg">
                  <span className="text-emerald-800">Accommodation Revenue</span>
                  <span className="font-bold text-emerald-700">87%</span>
                </div>
                <p className="text-xs text-gray-500 px-1">
                  13% platform commission deducted from your accommodation earnings under the host only model. Founding hosts keep more during their intro (0% for 90 days, then 10% for year one).
                </p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-700 mb-3">Referral Bonus</h4>
              <div className="space-y-2">
                <div className="flex justify-between p-3 bg-amber-50 rounded-lg">
                  <span className="text-amber-800">Earn from hosts you refer</span>
                  <span className="font-bold text-amber-700">3%</span>
                </div>
                <p className="text-xs text-gray-500 px-1">
                  3% of the accommodation price on each booking a host you referred takes, never more than Ariv's own fee on that booking (so nothing during a founding host's free first 90 days). Paid out of Ariv's cut, never from the referred host's earnings.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pending Setup Warning */}
      {!isOnboarded && summary?.totalPendingTransfer > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-6 w-6 text-red-600" />
              <div>
                <p className="font-semibold text-red-800">
                  You have {formatGBP(summary.totalPendingTransfer)} pending!
                </p>
                <p className="text-sm text-red-700">
                  Complete your payout setup to receive these funds automatically.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* VAT / Tax Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-[#1F3A68] text-lg flex items-center gap-2">
            <Shield className="h-5 w-5" /> Tax & VAT Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            If you are VAT registered, enter your VAT number below. This helps with UK tax compliance and will be shown on invoices.
          </p>
          <div className="flex items-end gap-3 max-w-md">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700">VAT Number</label>
              <Input
                value={vatNumber}
                onChange={e => setVatNumber(e.target.value)}
                placeholder="GB 123 4567 89"
                className="mt-1"
              />
            </div>
            <Button
              onClick={handleSaveVat}
              disabled={vatSaving}
              className="bg-[#1F3A68] hover:bg-[#1F3A68]/90"
            >
              {vatSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
          {!vatNumber && (
            <div className="flex items-start gap-2 mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700">Adding your VAT number is optional but recommended for compliance. You can add it at any time.</p>
            </div>
          )}
          <p className="text-xs text-gray-400 mt-2">Platform fees are currently shown excluding VAT. VAT will be applied when the platform reaches the £90,000 threshold.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PayoutsTab;
